import React from "react";
import { Card } from "react-bootstrap";
import "../../../../../styles/StayUptoDateCSS/SubComponents.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import track, {
  TRACKING_TYPES,
} from "../../../../../Utils/SupabaseServices/Track";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../../../../../Utils/SupabaseServices/TrackTemplate";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../../../../../Utils/analytics/legacyLibAnalyticsPath";
import { isPublicWebsiteTemplateRoute } from "../../../../../Utils/websiteTemplateGuards";
import { resolveTemplatePageKeyFromPathname } from "../../../MasterTemplateEditor/websitePublicTemplatePath";
import { updateRefClick } from "../../../../../Utils/SupabaseServices/ContentDatabase";
import DataSkeleton from "../../../../../components/StayUpToDateComponent/UpToDateComponents/DataSkeleton";
import { useDomLoaded } from "../../../../../hooks/DomLoadedContext";
import ProductName from "../../../../../components/ProductName/ProductName";
import ProductShopButton from "../../../../../components/ProductName/ProductShopButton";
import { useSellerDashboard } from "../../../../../components/Seller/hooks/useSellerDashboard";
import { Button } from "antd";
import { useSellerProductsByUsername } from "../../../../../hooks/useSellerProductsByUsername";

interface SellerProductDashboardProps {
  user_id: string;
  username: string;
  title: string;
  isLinks?: boolean;
  brand_color?: string;
  type?: string;
  cta?: string;
  from?: string;
  designTokens?: any;
}

const SellerProductDashboard: React.FC<SellerProductDashboardProps> = ({
  user_id,
  username,
  title,
  isLinks,
  brand_color,
  type,
  cta,
  from,
  designTokens
}) => {
  const location = useLocation();
  const domLoaded = useDomLoaded();
  const param = useParams();
  const { products } = useSellerProductsByUsername();
  const navigate = useNavigate();
  function handleRedirect(
    postId: string,
    media_url: string,
    platform: string,
    title: string,
    name: string,
    isLinks?: boolean
  ) {
    const details = {
      post_id: postId,
      media_url: media_url,
      title: title,
      name: name,
      platform: platform,
      ...(param?.id ? { id: param?.id } : {}),
    };
    const path = location.pathname || "";
    const pageKey =
      resolveTemplatePageKeyFromPathname(path) || "linkInBio";
    const libPath = legacyLibAnalyticsPathname(username);

    const sendClassic = (eventName: string, eventDetails: Record<string, unknown>) => {
      track(eventName, TRACKING_TYPES.EVENT, {
        pathname: libPath,
        details: eventDetails,
        user_id: user_id,
        title: "Stay Up To Date",
      });
    };

    if (isPublicWebsiteTemplateRoute(path)) {
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.POST_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        {
          pathname: path,
          details,
          user_id,
          username,
          page_key: pageKey,
          section_id: "products-carousel",
          section_type: "ProductsCarousel",
          entity_type: "product",
          entity_id: postId,
          entity_label: name || title,
        },
      );
      if (isLinks === true) {
        void trackTemplate(
          TEMPLATE_ANALYTICS_EVENTS.SHOP_CLICK,
          TEMPLATE_TRACKING_TYPES.EVENT,
          {
            pathname: path,
            details,
            user_id,
            username,
            page_key: pageKey,
            section_id: "products-carousel",
            section_type: "ProductsCarousel",
            entity_type: "product",
            entity_id: postId,
            entity_label: name || title,
          },
        );
      } else if (isLinks === false) {
        void trackTemplate(
          TEMPLATE_ANALYTICS_EVENTS.ORIGINAL_POST,
          TEMPLATE_TRACKING_TYPES.EVENT,
          {
            pathname: path,
            details,
            user_id,
            username,
            page_key: pageKey,
            entity_type: "post",
            entity_id: postId,
            entity_label: name || title,
          },
        );
      }
      if (shouldMirrorLegacyLibAnalytics(path, pageKey)) {
        sendClassic("Post Click", details);
        if (isLinks === true) sendClassic("Redirect to shop link", details);
        else if (isLinks === false) {
          sendClassic("Redirect to original post", details);
        }
      }
    } else {
      sendClassic("Post Click", details);
      if (isLinks === true) sendClassic("Redirect to shop link", details);
      else if (isLinks === false) {
        sendClassic("Redirect to original post", details);
      }
    }
    updateRefClick(postId, user_id);
  }

  return (
    <div>
      {(products && products.length === 0) || !domLoaded ? (
        <div className="outer-container-subcomponents">
          <DataSkeleton dashboard={true} />
        </div>
      ) : (
        <div className="outer-container-subcomponents">
          {products && products.length > 0 ? (
            <div className="links-grid-container-subcomponents">
              {products.map((product: any, index: number) => (
                <React.Fragment key={index}>
                  <a
                    href={product.url || "#"}
                    target="_blank"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRedirect(
                        product.id,
                        product.product_image,
                        "seller_product",
                        title,
                        product.name,
                        isLinks
                      );
                      if (product.url && product.url !== "") {
                        window.open(product.url, "_blank");
                      }
                    }}
                    style={{fontFamily:designTokens?.secondaryFontFamily}}
                  >
                    <div className={"creator-feed-image-wrapper-links"}>
                      <Card.Img
                        variant="top"
                        src={product.product_image}
                        className={"creator-feed-card-links-image"}
                        loading="eager"
                        style={{
                          backgroundColor: brand_color || undefined,
                        }}
                      />
                    </div>

                    <p className="product-name" style={{fontFamily:designTokens?.secondaryFontFamily}}>{product.name}</p>
                    <Button
                      className="template-shop-btn-links"
                      style={{ marginTop: "8px", fontFamily:designTokens?.secondaryFontFamily }}
                      onClick={() => {
                        navigate(`/seller-product/${product.id}`);
                      }}
                    >
                      Shop
                    </Button>
                  </a>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="outer-container-subcomponents">
              <p>No products available at this time</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SellerProductDashboard;
